import { useCallback } from 'react';
import { usePersonData } from '@/hooks/api/person';
import { buildDataLayer, isDataLayerComplete } from './analytics.data-layer';
import type { TAnalyticsDataLayer, TAnalyticsEvent, TUseAnalyticsReturn } from './analytics.types';

const ADOBE_LAUNCH_HISTORY_KEY = 'adobeLaunchHistory';
const DEFAULT_EVENT_NAME = 'customPageView';

const readHistory = (): string[] | null => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(ADOBE_LAUNCH_HISTORY_KEY);
		return raw ? (JSON.parse(raw) as string[]) : null;
	} catch {
		return null;
	}
};

const writeHistory = (history: string[]): void => {
	if (typeof window === 'undefined') return;
	window.localStorage.setItem(ADOBE_LAUNCH_HISTORY_KEY, JSON.stringify(history));
};

/**
 * Limpa o histórico de dedupe de page views na inicialização do app —
 * espelha o `withAdobeLaunch` da garagem (`localStorage.removeItem` no mount).
 *
 * É essencial porque o `adobeLaunchHistory` é compartilhado por todos os apps
 * do mesmo origin (garagem, chat, dados...) e este app reusa os pageNames da
 * garagem (ex.: `/webmotors/garagem/meu-perfil`). Sem limpar, um pageName já
 * gravado pela garagem faz a dedupe pular o dispatch aqui — o evento nunca é
 * enviado ao Adobe. Limpar no boot garante que cada carga do app comece zerada.
 */
const clearAnalyticsHistory = (): void => {
	if (typeof window === 'undefined') return;
	window.localStorage.removeItem(ADOBE_LAUNCH_HISTORY_KEY);
};

const dispatchAdobeEvent = (
	eventName: string,
	dataLayer: TAnalyticsDataLayer,
	storage: string[] | null
): void => {
	if (typeof window === 'undefined' || typeof document === 'undefined') return;
	window.objDataLayer = dataLayer as unknown as Record<string, unknown>;
	document.dispatchEvent(new CustomEvent(eventName, { detail: window.objDataLayer }));
	if (storage !== null && eventName === DEFAULT_EVENT_NAME) {
		writeHistory(storage);
	}
};

/**
 * Hook de analytics para Adobe Launch.
 *
 * Espelha o `withAdobeLaunch` da garagem:
 *  1. Monta `objDataLayer` a partir do evento + dados de `usePersonData`
 *  2. Verifica se todas as seções pedidas (`use`) têm dados (`isFinished`)
 *  3. Faz dedupe de page views via `localStorage.adobeLaunchHistory`
 *  4. Publica em `window.objDataLayer` e dispara `CustomEvent` no `document`
 *
 * O script do Adobe Launch é injetado pelo `adobeLaunchPlugin` do vite quando
 * `VITE_ADOBE_LAUNCH_URL` está definida — sem essa URL os eventos viram no-op.
 *
 * Uso:
 *   const { trackPageView, trackClick } = useAnalytics();
 *   trackPageView({ pageName, pageType, flowType, use, tier });
 *   trackClick({ eventName: 'cliqueEditarGaragem', ... });
 */
const useAnalytics = (): TUseAnalyticsReturn => {
	const { person } = usePersonData();

	const dispatch = useCallback(
		(event: TAnalyticsEvent): boolean => {
			const dataLayer = buildDataLayer(event, person);
			if (!isDataLayerComplete(event, dataLayer)) return false;

			const eventName = event.eventName ?? DEFAULT_EVENT_NAME;
			const history = readHistory();

			if (history) {
				const alreadySeen = history.includes(event.pageName);
				const isPageView = eventName === DEFAULT_EVENT_NAME;
				if (alreadySeen && isPageView) return false;

				const nextStorage = event.recurringEvent ? history : [...history, event.pageName];
				dispatchAdobeEvent(eventName, dataLayer, nextStorage);
				return true;
			}

			dispatchAdobeEvent(eventName, dataLayer, event.recurringEvent ? null : []);
			return true;
		},
		[person]
	);

	const trackPageView = useCallback(
		(event: TAnalyticsEvent): void => {
			dispatch({ ...event, eventName: undefined });
		},
		[dispatch]
	);

	const trackClick = useCallback(
		(event: TAnalyticsEvent): void => {
			if (!event.eventName) return;
			dispatch({ ...event, recurringEvent: event.recurringEvent ?? true });
		},
		[dispatch]
	);

	const trackEvent = useCallback(
		(eventName: string, event: TAnalyticsEvent): void => {
			dispatch({ ...event, eventName });
		},
		[dispatch]
	);

	return { trackPageView, trackClick, trackEvent };
};

export { useAnalytics, clearAnalyticsHistory };
